import {
    takeLatest,
    put,
    call,
    select,
    fork,
    take,
    cancel,
    actionChannel,
    delay,
} from 'redux-saga/effects';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import {
    resetPassword,
    setPassword,
    getEmailByAuthToken,
    requestOtpForResetPassword,
    verifyOtpForResetPassword,
    setNewPasswordUsingPhoneNumber,
} from 'yoda-interfaces/lib/Account/AccountApi';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import errorsCodes from 'yoda-account-components/lib/common/errorsCodes';
import * as PasswordResetActionTypes from '../actionTypes/PasswordResetActionTypes';
import { getAccertifyDeviceInfo, logExtendedAnalytics } from './SiteProbingSaga';
import {
    customStepUp,
    openSignInSlider,
    initiateSignInSliderClose,
} from '../actions/SignInSliderActions';
import { INITIATE_SIGNIN_SLIDER_CLOSE } from '../actionTypes/SignInSliderActionTypes';
import {
    resetPasswordSliderState,
    passwordResetByPhoneInitiatedAnalytics,
} from '../actions/PasswordResetAction';
import { selectAutoLoginThrottleFF } from '../selectors/resetPasswordSelector';
import FactorySaga from './FactorySaga';
import {
    selectFeatureFlags,
    selectPreferences,
    selectMessagesTexts,
} from '../selectors/ContextSelector';
import { storeSignInSuccessInfo } from './SigninSaga';
// import * as SignInSliderActionTypes from '../actionTypes/SignInSliderActionTypes';

const defaults = {
    signInSliderDetails: {},
};

export const getSignInSliderDetails = (state) =>
    state.signInSliderDetails || defaults.signInSliderDetails;

export function* passwordResetSaga(action) {
    try {
        let passwordResetEmail = action.payload;
        const isSignInSliderReq = get(action.payload, 'isSignInSliderReq', false);
        const successAction = PasswordResetActionTypes.PASSWORD_RESET_SUCCESS;
        const errorAction = PasswordResetActionTypes.PASSWORD_RESET_ERROR;
        let nextURL = '';
        let pathname = '';
        if (!__SERVER__) {
            pathname = window.location.pathname;
            nextURL = window.location.pathname + window.location.search;
        }
        const preferences = yield select(selectPreferences);
        const { enableADIForgotPassword = true } = yield select(selectFeatureFlags) || {};

        const passwordResetException = get(preferences, 'passwordResetException', {});

        if (passwordResetException[pathname]) {
            nextURL = passwordResetException[pathname];
        }

        if (isSignInSliderReq) {
            // successAction = SignInSliderActionTypes.SIGNIN_SLIDER_PASSWORD_RESET_SUCCESS;
            // errorAction = SignInSliderActionTypes.SIGNIN_SLIDER_PASSWORD_RESET_ERROR;
            passwordResetEmail = get(action, 'payload.email', '');
        }
        const reqPayload = {
            type: 'forgot-password',
            user: {
                emailAddress: passwordResetEmail,
            },
            nextURL: nextURL || get(passwordResetException, 'DEFAULT', '/account/dashboard'),
        };
        if (enableADIForgotPassword) {
            const deviceInfo = yield call(getAccertifyDeviceInfo, {
                actionName: isSignInSliderReq ? 'slider_forgot_pwd' : 'cam_forgot_pwd',
                enableFeatureSpecificFlag: enableADIForgotPassword,
            });
            if (deviceInfo) {
                reqPayload.deviceInfo = deviceInfo;
            }
        }
        const response = yield call(resetPassword, reqPayload);
        const payload = {
            response: response.data,
        };

        payload.isLoading = false;
        payload.passwordResetEmail = passwordResetEmail;

        const adiStatus = response?.headers?.get('x-adi-sts');
        if (enableADIForgotPassword && ['A', 'R'].includes(adiStatus)) {
            yield call(logExtendedAnalytics, {
                result: { response },
                isTriggerFromLS: false,
                actionMethod: isSignInSliderReq ? 'SLIDER_FORGOT_PASSWORD' : 'CAM_FORGOT_PASSWORD',
            });
        }
        if (response.status === 200 || response.status === 201) {
            payload.isSuccess = true;
            yield put({ type: successAction, payload });
        } else {
            let error = get(response, 'data', {});
            if (isEmpty(error)) {
                error = {
                    errorCode: 'DEFAULT_EXCEPTION_WHILE_RESETTING_PASSWORD',
                };
            }
            yield put({
                type: errorAction,
                payload: { response: error, isSuccess: false },
            });
        }
    } catch (error) {
        const err = {
            errorCode: 'DEFAULT_EXCEPTION_WHILE_RESETTING_PASSWORD',
        };
        yield put({
            type: PasswordResetActionTypes.PASSWORD_RESET_ERROR,
            payload: { response: err, isSuccess: false },
        });
    }
}

function* handleADIResponseforAnalytics(response, actionMethod) {
    const { enableADIResetPassword = true } = yield select(selectFeatureFlags) || {};
    const adiStatus = response?.headers?.get('x-adi-sts');
    if (enableADIResetPassword && ['A', 'R'].includes(adiStatus)) {
        yield call(logExtendedAnalytics, {
            result: { response },
            isTriggerFromLS: false,
            actionMethod,
        });
    }
}

export function* setPasswordSaga(action) {
    try {
        const { email, ...params } = action.payload;
        const {
            enableADIResetPassword = true,
            enableUpdateProfileLegacyPayload = false,
        } = yield select(selectFeatureFlags) || {};
        params.queryParams = [];
        const queryParams = {
            op: 'replace',
            path: '/password',
            value: params.password,
        };
        params.queryParams.push(queryParams);
        if (!enableUpdateProfileLegacyPayload) {
            params.queryParams = { patches: params.queryParams };
        }
        if (enableADIResetPassword) {
            const deviceInfo = yield call(getAccertifyDeviceInfo, {
                actionName: 'CAM_reset_password_page',
                enableFeatureSpecificFlag: enableADIResetPassword,
            });
            if (deviceInfo) {
                params.queryParams.deviceInfo = deviceInfo;
            }
        }
        const isAutoLoginEnabled = yield select(selectAutoLoginThrottleFF);
        const accountId = TokenProvider.get('ACCOUNT_ID');
        let customHeaders = {};
        if (isAutoLoginEnabled) {
            customHeaders = {
                ...(email && { 'X-SRC-EMAIL': email }),
                ...(accountId && { 'X-GUEST-ACCOUNT-ID': accountId, 'X-SRC-ACC-ID': accountId }),
            };
        }
        params.isDPOrder = isAutoLoginEnabled;
        const response = yield call(setPassword, params, customHeaders);
        const payload = {
            response: response.data,
        };
        // ADI Analytics handler
        if (enableADIResetPassword) {
            yield call(handleADIResponseforAnalytics, response, 'CAM_RESET_PASSWORD');
        }
        payload.isLoading = false;
        if (response.status === 204 || response.status === 200) {
            payload.isSuccess = true;
            yield put({ type: PasswordResetActionTypes.PASSWORD_SET_SUCCESS, payload });
            if (isAutoLoginEnabled && response.status === 200 && get(response, 'data.account_id')) {
                yield fork(storeSignInSuccessInfo, { response, email });
            }
        } else {
            let error = get(response, 'data', {});
            if (isEmpty(error)) {
                error = {
                    errorCode: 'DEFAULT_EXCEPTION_WHILE_RESETTING_PASSWORD',
                };
            }
            const isMonitoringMode = response?.headers?.get('x-adi-monitor');
            const adiStatus = response?.headers?.get('x-adi-sts');
            /*  formError event was already triggered from inside logExtendedAnalytics
                in the case of enforced ADI Reject. */
            const isFormErrorEventTriggered =
                enableADIResetPassword && adiStatus === 'R' && !isMonitoringMode;
            error.isADIEnable = !!enableADIResetPassword;
            if (!isFormErrorEventTriggered)
                yield put({
                    type: PasswordResetActionTypes.PASSWORD_SET_ERROR,
                    payload: { response: error, isSuccess: false },
                });
            yield put({
                type: PasswordResetActionTypes.PASSWORD_SET_FAILURE,
                payload: { response: error, isSuccess: false },
            });
        }
    } catch (error) {
        const err = {
            errorCode: 'DEFAULT_EXCEPTION_WHILE_SETTING_PASSWORD',
        };
        yield put({
            type: PasswordResetActionTypes.PASSWORD_SET_ERROR,
            payload: { response: err, isSuccess: false },
        });
    }
}

export function* getEmailByAuthTokenSaga(action) {
    try {
        const response = yield call(getEmailByAuthToken, action.payload);

        if (response && response.status === 200) {
            yield put({ type: PasswordResetActionTypes.GET_ACCOUNT_EMAIL_SUCCESS, response });
        } else {
            yield put({
                type: PasswordResetActionTypes.GET_ACCOUNT_EMAIL_ERROR,
                errorResponse: response.data,
            });
        }
    } catch (error) {
        yield put({
            type: PasswordResetActionTypes.GET_ACCOUNT_EMAIL_ERROR,
            errorResponse: error,
        });
    }
}

function* enableAferDelay() {
    try {
        // get time from config
        const preferences = yield select(selectPreferences);
        const parsedNumber = Number(get(preferences, 'phoneVerification.resendEnableTime', '5000'));
        const DELAY = parsedNumber || 5000;
        yield delay(DELAY);
        yield put({ type: PasswordResetActionTypes.ENABLE_RESEND_OTP_TO_RESET_PASSWORD });
    } catch (err) {
        console.error('some error occured:', err);
    }
}

function* delayedEnableResendTask() {
    try {
        const bgTask = yield fork(enableAferDelay);

        const requestChan = yield actionChannel([
            PasswordResetActionTypes.POST_REQUEST_OTP_TO_RESET_PASSWORD_ERROR,
            PasswordResetActionTypes.VERIFY_OTP_TO_RESET_PASSWORD,
            PasswordResetActionTypes.VERIFY_OTP_TO_RESET_PASSWORD_SUCCESS,
        ]);
        while (true) {
            // NOSONAR
            // wait for any actions to trigger
            // or delay to complete
            yield take(requestChan);
            yield cancel(bgTask);
            yield put({ type: PasswordResetActionTypes.ENABLE_RESEND_OTP_TO_RESET_PASSWORD });
            // break;
        }
    } catch (err) {
        console.error('some error occured:', err);
    }
}

export function* requestOTPToResetPasswordSaga(action) {
    try {
        const phoneNumber = get(action, 'payload.phoneNumber');
        const isSignInSliderReq = get(action, 'payload.isSignInSliderReq');
        const messagesTexts = yield select(selectMessagesTexts);
        if (!phoneNumber) {
            yield put({
                type: PasswordResetActionTypes.POST_REQUEST_OTP_TO_RESET_PASSWORD_ERROR,
                eventName: get(messagesTexts, 'passwordReset.requestOTPError'),
            });
            return;
        }
        yield put(
            passwordResetByPhoneInitiatedAnalytics(
                get(messagesTexts, 'passwordReset.resetByPhoneInitiated', '')
            )
        );
        const response = yield call(FactorySaga, requestOtpForResetPassword, {
            payload: { phoneNumber },
        });
        if (response.isSuccess) {
            const isReSendRequest = get(action, 'payload.isReSendRequest');
            yield put({
                type: PasswordResetActionTypes.POST_REQUEST_OTP_TO_RESET_PASSWORD_SUCCESS,
                eventName: get(messagesTexts, 'passwordReset.requestOTPSuccess'),
                payload: { phoneNumber, isReSendRequest, isSignInSliderReq },
            });
            if (!action.payload.isSignInSliderReq) {
                yield put(openSignInSlider(false, '', false, true));
            }
            yield fork(delayedEnableResendTask);
        } else {
            let error = get(response, 'response.data', {});
            if (isEmpty(error)) {
                error = {
                    errorMessage: errorsCodes.DEFAULT.title,
                    errorCode: 'PHONE_VERIFICATION_DEFAULT_EXCEPTION_WHILE_RESETTING_PASSWORD',
                };
            }
            yield put({
                type: PasswordResetActionTypes.POST_REQUEST_OTP_TO_RESET_PASSWORD_ERROR,
                eventName: get(messagesTexts, 'passwordReset.requestOTPError'),
                payload: { response: error, isSuccess: false },
            });
        }
    } catch (error) {
        const messagesTexts = yield select(selectMessagesTexts);
        const err = {
            errorMessage: error.message || errorsCodes.DEFAULT.title,
            errorCode: 'PHONE_VERIFICATION_DEFAULT_EXCEPTION_WHILE_RESETTING_PASSWORD',
        };
        yield put({
            type: PasswordResetActionTypes.POST_REQUEST_OTP_TO_RESET_PASSWORD_ERROR,
            eventName: get(messagesTexts, 'passwordReset.requestOTPError'),
            payload: { response: err, isSuccess: false },
        });
    }
}
export function* verifyOtpToResetPassword(action) {
    try {
        const phoneNumber = get(action, 'payload.phoneNumber');
        const messagesTexts = yield select(selectMessagesTexts);
        if (!phoneNumber) {
            yield put({
                type: PasswordResetActionTypes.POST_REQUEST_OTP_TO_RESET_PASSWORD_ERROR,
                eventName: get(messagesTexts, 'passwordReset.requestOTPError'),
            });
            return;
        }
        const response = yield call(FactorySaga, verifyOtpForResetPassword, action);
        if (response.isSuccess) {
            yield put({
                type: PasswordResetActionTypes.VERIFY_OTP_TO_RESET_PASSWORD_SUCCESS,
                eventName: get(messagesTexts, 'passwordReset.verifyOTPSuccess'),
                payload: response,
            });
        } else {
            let error = get(response, 'response.data', {});
            if (isEmpty(error)) {
                error = {
                    errorMessage: errorsCodes.DEFAULT.title,
                    errorCode: 'OTP_VERIFICATION_DEFAULT_EXCEPTION_WHILE_RESETTING_PASSWORD',
                };
            }
            yield put({
                type: PasswordResetActionTypes.VERIFY_OTP_TO_RESET_PASSWORD_ERROR,
                eventName: get(messagesTexts, 'passwordReset.verifyOTPError'),
                payload: error,
            });
        }
    } catch (error) {
        const messagesTexts = yield select(selectMessagesTexts);
        const errorPayload = {
            error: {
                errorMessage: error.message || errorsCodes.DEFAULT.title,
                errorCode: 'OTP_VERIFICATION_DEFAULT_EXCEPTION_WHILE_RESETTING_PASSWORD',
            },
        };
        yield put({
            type: PasswordResetActionTypes.VERIFY_OTP_TO_RESET_PASSWORD_ERROR,
            eventName: get(messagesTexts, 'passwordReset.verifyOTPError'),
            payload: errorPayload,
        });
    }
}

export function* onAutoLoginSuccess() {
    try {
        const signInSliderDetails = yield select(getSignInSliderDetails);
        let redirectUrl = signInSliderDetails.postAuthenticationUrl;
        yield put(initiateSignInSliderClose());
        const featureFlags = yield select(selectFeatureFlags);
        const preferences = yield select(selectPreferences);
        const currentPage =
            !__SERVER__ && window.location.pathname ? window.location.pathname : undefined;
        if (
            featureFlags.enableSignInSliderAuthRedirects &&
            redirectUrl === undefined &&
            get(preferences, `authenticationSuccessRedirects[${currentPage}]`) === undefined
        ) {
            redirectUrl = preferences.signInSliderAuthRedirects[currentPage];
        }
        yield put(resetPasswordSliderState());
        if (redirectUrl) {
            window.location.assign(redirectUrl);
        }
    } catch (error) {
        console.log('onAutoLoginSuccess error: ', error);
    }
}

export function* setNewPasswordUsingPhone(action) {
    try {
        const { phoneNumber, otp, password } = get(action, 'payload', {});
        if (!phoneNumber || !otp || !password) {
            yield put({ type: PasswordResetActionTypes.SET_NEW_PASSWORD_USING_PHONE_NUMBER_ERROR });
            return;
        }
        const isAutoLoginEnabled = yield select(selectAutoLoginThrottleFF);
        const accountId = TokenProvider.get('ACCOUNT_ID');
        if (isAutoLoginEnabled && accountId) {
            action = {
                ...action,
                ...(accountId && { guestAccountId: accountId, isXSourceAccountId: accountId }),
                isDPOrder: true,
            };
        }
        const response = yield call(setNewPasswordUsingPhoneNumber, action);
        const messagesTexts = yield select(selectMessagesTexts);
        if (response.status === 204 || response.status === 200) {
            yield put({
                type: PasswordResetActionTypes.SET_NEW_PASSWORD_USING_PHONE_NUMBER_SUCCESS,
                eventName: get(messagesTexts, 'passwordReset.resetByPhoneSuccess', ''),
                payload: response,
            });
            const signInSliderDetails = yield select(getSignInSliderDetails);
            if (isAutoLoginEnabled && get(response, 'data.account_id')) {
                yield call(storeSignInSuccessInfo, { response, email: phoneNumber });
                const { type } = yield take(INITIATE_SIGNIN_SLIDER_CLOSE);
                switch (type) {
                    case INITIATE_SIGNIN_SLIDER_CLOSE: {
                        yield fork(onAutoLoginSuccess);
                        break;
                    }
                    default:
                        break;
                }
            } else {
                yield put(
                    customStepUp(
                        {
                            skipOnAuthenticationSuccess: true,
                            postAuthenticationUrl: signInSliderDetails.postAuthenticationUrl,
                            ensureRestricted: true,
                            greetMessage: get(
                                messagesTexts,
                                'passwordReset.successfulSignInDescription',
                                "You're all set! Use your new password to sign in to your account."
                            ),
                            passwordUpdated: true,
                            passwordUpdatedTitle: get(
                                messagesTexts,
                                'passwordReset.successfulSignInTitle',
                                'Password Updated'
                            ),
                        },
                        {},
                        { userName: phoneNumber }
                    )
                );
                yield put(resetPasswordSliderState());
            }
        } else {
            let error = get(response, 'data', {});
            if (isEmpty(error)) {
                error = {
                    errorMessage: errorsCodes.DEFAULT.title,
                    errorCode: 'DEFAULT_EXCEPTION_WHILE_RESETTING_PASSWORD_BY_PHONE',
                };
            }
            yield put({
                type: PasswordResetActionTypes.SET_NEW_PASSWORD_USING_PHONE_NUMBER_ERROR,
                payload: error,
                eventName: get(messagesTexts, 'passwordReset.resetByPhoneError'),
            });
        }
    } catch (error) {
        const messagesTexts = yield select(selectMessagesTexts);
        const errorPayload = {
            error: {
                errorMessage: error.message || errorsCodes.DEFAULT.title,
                errorCode: 'DEFAULT_EXCEPTION_WHILE_RESETTING_PASSWORD_BY_PHONE',
            },
        };
        yield put({
            type: PasswordResetActionTypes.SET_NEW_PASSWORD_USING_PHONE_NUMBER_ERROR,
            payload: errorPayload,
            eventName: get(messagesTexts, 'passwordReset.resetByPhoneError'),
        });
    }
}

function* watchPasswordResetSaga() {
    yield takeLatest(PasswordResetActionTypes.PASSWORD_RESET, passwordResetSaga);
    yield takeLatest(PasswordResetActionTypes.PASSWORD_SET, setPasswordSaga);
    yield takeLatest(PasswordResetActionTypes.GET_ACCOUNT_EMAIL, getEmailByAuthTokenSaga);
    yield takeLatest(
        PasswordResetActionTypes.POST_REQUEST_OTP_TO_RESET_PASSWORD,
        requestOTPToResetPasswordSaga
    );
    yield takeLatest(
        PasswordResetActionTypes.VERIFY_OTP_TO_RESET_PASSWORD,
        verifyOtpToResetPassword
    );
    yield takeLatest(
        PasswordResetActionTypes.SET_NEW_PASSWORD_USING_PHONE_NUMBER,
        setNewPasswordUsingPhone
    );
}
watchPasswordResetSaga.sagaName = 'passwordResetSaga';
export default watchPasswordResetSaga;
